import { CircularProgress, RadioCardItem } from '@civilgrid/ui/components';
import Dinero from 'dinero.js';
import { isNumber } from 'lodash';
import { useMemo } from 'react';
import type { AsyncState } from 'react-use/lib/useAsyncFn';
import type { GetCostOutput } from '~/common/billingUtils';
import type { Project } from '~/common/types';
import InfoTooltip from '~/components2/InfoTooltip';

interface Props {
  billingTier: Project['billingTier'];
  label: string;
  cost?: AsyncState<GetCostOutput>;
  disabled?: boolean;
  zeroCostLabel?: string;
  currentBillingTier?: Project['billingTier'];
  'data-testid'?: string;
}

const descriptionMap = {
  draft: '',
  proposal: '',
  project: 'Full Utility Research',
  projectPlus: 'Unlimited Digitization',
};

export default function ProjectTypePill({
  billingTier,
  label,
  cost,
  disabled,
  zeroCostLabel = ' ',
  currentBillingTier,
  'data-testid': dataTestId,
}: Props) {
  const costLabel = useMemo(() => {
    if (cost) {
      if (cost.loading) {
        return <CircularProgress indeterminate />;
      }
      if (cost.value?.[billingTier].totalCost === 0) {
        return zeroCostLabel;
      }

      if (currentBillingTier && currentBillingTier !== billingTier) {
        const currentCostAmount = cost.value?.[currentBillingTier]?.totalCost;
        const newCostAmount = cost.value?.[billingTier]?.totalCost;

        if (isNumber(currentCostAmount) && isNumber(newCostAmount)) {
          const currentCost = Dinero({ amount: currentCostAmount ?? 0 });
          const newCost = Dinero({ amount: newCostAmount ?? 0 });
          const costDifference = newCost.subtract(currentCost);

          return (
            <>
              <span className="block">{costDifference.toFormat()}</span>
              <span className="flex items-center gap-1 text-xs text-secondary line-through">
                {newCost.toFormat()}
                <InfoTooltip text={`Includes ${currentCost.toFormat()} credit`} label="About credit" />
              </span>
            </>
          );
        }

        return '';
      }

      if (isNumber(cost.value?.[billingTier]?.totalCost)) {
        return Dinero({
          amount: cost.value?.[billingTier]?.totalCost,
        }).toFormat();
      }

      return '';
    } else {
      return '';
    }
  }, [cost, billingTier, currentBillingTier, zeroCostLabel]);

  const description = useMemo(() => {
    if (!cost || cost.loading) {
      return undefined;
    }
    const tierTotal = cost.value?.[billingTier]?.totalCost;
    return isNumber(tierTotal) && tierTotal > 0 ? descriptionMap[billingTier] : undefined;
  }, [cost, billingTier]);

  return (
    <RadioCardItem
      value={billingTier}
      label={label}
      detail={costLabel}
      description={description}
      disabled={disabled}
      data-testid={dataTestId}
    />
  );
}
